import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Target, Heart, ArrowRight, Bike, Activity, Globe, Award, Zap, TreePine, Shield } from "lucide-react";

function VugaNange() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const programAreas = [
    {
      icon: Bike,
      title: "Inclusive Cycling",
      description: "Creating opportunities for all community members to participate in regular cycling activities",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Users,
      title: "Community Engagement",
      description: "Building strong community connections through cycling programs and group activities",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Zap,
      title: "Behavior Change",
      description: "Using cycling as a tool to promote positive lifestyle and community behavior changes",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Shield,
      title: "Trusted Partnership",
      description: "Experienced development team providing reliable support for cycling goals and initiatives",
      color: "from-orange-500 to-red-500"
    }
  ];

  const benefits = [
    { icon: Heart, title: "Health & Wellness", description: "Promoting physical fitness and mental wellbeing through cycling" },
    { icon: TreePine, title: "Environmental Impact", description: "Encouraging sustainable transportation and environmental awareness" },
    { icon: Globe, title: "Community Connection", description: "Building social bonds and community cohesion through shared activities" },
    { icon: Award, title: "Youth Development", description: "Empowering young people through cycling skills and leadership opportunities" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-cyan-50">
      <Header />
      
      {/* Hero Image Section */}
      <div className="relative pt-24 sm:pt-28">
        <div className="w-full h-64 md:h-96 lg:h-[400px] overflow-hidden">
          <img 
            src="img/cycle.jpg" 
            className="w-full h-full object-cover object-center"
            alt="Vuga Nange Cycling Program"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Hero Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center pt-20 sm:pt-24">
          <div className="text-center text-white px-4">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Bike className="w-4 h-4 mr-2" />
              Youth Cycling Charity Program
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-green-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Vuga Nange
              </span>
              <br />
              <span className="text-white">Community Project</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Pedaling toward positive change and community connection
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="mission-content"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['mission-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-8">
              Cycling for <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Change</span>
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-green-100">
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-6">
                  <strong className="text-green-700">Vuga Nange Community Project</strong> is an innovative 
                  youth cycling charity program that promotes inclusive cycling through successful 
                  community engagement initiatives, giving people regular opportunities to cycle 
                  with comprehensive help, support, and guidance.
                </p>
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-6 rounded-r-2xl">
                  <p className="text-lg text-gray-700 font-medium">
                    Through our experienced development team, we utilize cycling as a powerful tool 
                    to achieve positive behavior change and strengthen community bonds across local neighborhoods.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Program Areas Grid */}
          <div 
            id="program-grid"
            data-animate
            className={`mb-16 transform transition-all duration-1000 delay-300 ${
              isVisible['program-grid'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Our <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Approach</span>
              </h3>
              <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {programAreas.map((area, index) => {
                const Icon = area.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${area.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-800 mb-4 text-center">{area.title}</h4>
                    <p className="text-gray-600 text-center leading-relaxed">{area.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Experience Section */}
          <div 
            id="experience-section"
            data-animate
            className={`mb-16 transform transition-all duration-1000 delay-500 ${
              isVisible['experience-section'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-3xl p-1 shadow-2xl">
              <div className="bg-white rounded-3xl p-8 md:p-12">
                <div className="text-center mb-8">
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                    Experienced <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Development Team</span>
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">Community Engagement Expertise</h4>
                        <p className="text-gray-600">Years of experience in building meaningful community connections and engagement programs.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Activity className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">Behavior Change Initiatives</h4>
                        <p className="text-gray-600">Proven track record in implementing successful behavior change programs through cycling.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6">
                    <h4 className="text-xl font-bold text-gray-800 mb-4">Trusted Partnership</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Our cycling development team serves as a reliable partner in supporting your cycling goals. 
                      Our well-connected officers understand the requirements to increase cycling participation 
                      and bring about positive community change through strategic, evidence-based interventions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div 
            id="benefits-grid"
            data-animate
            className={`transform transition-all duration-1000 delay-700 ${
              isVisible['benefits-grid'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Community <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Benefits</span>
              </h3>
              <p className="text-xl text-gray-600">The positive impact of cycling on our communities</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h4>
                        <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-green-600 via-blue-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <Bike className="w-5 h-5 mr-3" />
              Cycle • Connect • Change
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Join the Cycling Movement
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Partner with us to promote inclusive cycling, build stronger communities, 
              and create positive behavior change through the power of cycling. 
              Together, we can pedal toward a healthier, more connected future.
            </p>
            
            <button 
              onClick={() => window.location.href = '/contact-us'}
              className="bg-white text-green-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
            >
              Get Cycling <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default VugaNange;